import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Grid,
  Select,
  Heading,
  IconButton,
  Box,
  VStack,
  Stack,
  FormErrorMessage,
  Text,
} from "@chakra-ui/react";
import { X } from "lucide-react";
import { Procedures } from "@/types/procedures";
import { procedureService } from "@/lib/services/procedureService";

interface NewProcedureModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (procedure: Procedures) => void;
  editingProcedure?: Procedures | null;
  setProcedures?: React.Dispatch<React.SetStateAction<Procedures[]>>;
}

interface ErrorMessagesType {
  [key: string]: { message: string };
}

const NewProcedureModal: React.FC<NewProcedureModalProps> = ({
  isOpen,
  onClose,
  onSave,
  editingProcedure = null,
  setProcedures = () => {},
}) => {
  const [procedureName, setProcedureName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [status, setStatus] = useState<Procedures["status"]>("active");

  // Error messages state
  const [errorMessages, setErrorMessages] = useState<ErrorMessagesType>({});

  // Dummy ref to prevent auto focus
  const initialRef = useRef<HTMLDivElement>(null);

  // Pre-fill form when editing
  useEffect(() => {
    if (isOpen && editingProcedure) {
      setProcedureName(editingProcedure.name);
      setDescription(editingProcedure.description);
      setPrice(editingProcedure.price.toString());

      // Handle status conversion
      const procedureStatus = editingProcedure.status;
      if (procedureStatus === "active" || procedureStatus === 1) {
        setStatus("active");
      } else if (procedureStatus === "inactive" || procedureStatus === 0) {
        setStatus("inactive");
      } else {
        setStatus(procedureStatus as Procedures["status"]);
      }
    } else if (!isOpen) {
      // Reset modal when closed
      resetForm();
    }
  }, [isOpen, editingProcedure]);

  const resetForm = () => {
    setProcedureName("");
    setDescription("");
    setPrice("");
    setStatus("active");
    setErrorMessages({});
  };

  const validateForm = () => {
    const errors: ErrorMessagesType = {};

    if (!procedureName.trim()) {
      errors.procedureName = { message: "Procedure name is required" };
    }
    if (!price || Number(price) < 0) {
      errors.price = { message: "Valid price is required" };
    }

    if (!description) {
      errors.description = { message: "Description is required" };
    }

    setErrorMessages(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }
    
    if(editingProcedure) {
      if(editingProcedure.id) {
        await procedureService.updateProcedure(editingProcedure.id, {
          name: procedureName,
          description,
          price: Number(price),
          status: status,
        });
        if (setProcedures) {
          setProcedures((prev: Procedures[]) => prev.map((p: Procedures) => p.id === editingProcedure.id ? { ...p, name: procedureName, description, price: Number(price), status } : p));
        }
      }
    } else {
      onSave({
        name: procedureName,
        description,
        price: Number(price),
        status: status,
      });
    }
    
    onClose();
    resetForm();
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      isCentered
      size={{ base: "full", sm: "md", md: "lg", lg: "xl" }}
      scrollBehavior="inside"
      initialFocusRef={initialRef}
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent
        borderRadius={{ base: 0, md: "none" }}
        mx={{ base: 0, md: 4 }}
        maxH={{ base: "100vh", md: "90vh" }}
      >
        <ModalHeader
          fontSize={{ base: "lg", md: "xl" }}
          py={{ base: 3, md: 4 }}
          px={{ base: 4, md: 6 }}
          position="relative"
        >
          {editingProcedure ? "Edit Procedure" : "New Procedure"}
          {/* Close Button */}
          <IconButton
            aria-label="Close Modal"
            icon={<X size={18} />}
            onClick={handleClose}
            position="absolute"
            right={{ base: 3, md: 5 }}
            top={{ base: 3, md: 5 }}
            size={{ base: "sm", md: "md" }}
            variant="link"
            color="#0A1B39"
            _hover={{ color: "#DC3611" }}
            _focus={{ boxShadow: "none" }}
          />
        </ModalHeader>

        <Box borderBottom="1px solid #E7E8EB" mx={{ base: 4, md: 6 }} />

        <ModalBody
          py={{ base: 3, md: 4 }}
          px={{ base: 4, md: 6 }}
          overflowY="auto"
        >
          {/* Dummy div for initial focus */}
          <div ref={initialRef} tabIndex={-1} />

          <Heading
            size={{ base: "xs", sm: "sm", md: "sm" }}
            mb={{ base: 3, md: 4 }}
            fontWeight="semibold"
            color="#0A1B39"
          >
            Procedure Information
          </Heading>

          <VStack spacing={{ base: 3, md: 4 }} align="stretch">
            {/* Mobile: Stacked layout */}
            <Box display={{ base: "block", sm: "none" }}>
              <VStack spacing={3} align="stretch">
                {/* Procedure Name */}
                <FormControl
                  isRequired
                  isInvalid={!!errorMessages.procedureName}
                >
                  <FormLabel
                    fontSize="xs"
                    fontWeight="400"
                    color="#0A1B39"
                    mb={1}
                  >
                    Procedure Name{" "}
                    <Text as="span" color="red.500">
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    placeholder="Enter procedure name"
                    size="sm"
                    fontSize="xs"
                    bg="white"
                    borderRadius="md"
                    borderColor="gray.200"
                    h="36px"
                    minH="36px"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    value={procedureName}
                    onChange={(e) => setProcedureName(e.target.value)}
                  />
                  <FormErrorMessage fontSize="xs">
                    {errorMessages.procedureName?.message}
                  </FormErrorMessage>
                </FormControl>

                {/* Price */}
                <FormControl isRequired isInvalid={!!errorMessages.price}>
                  <FormLabel
                    fontSize="xs"
                    fontWeight="400"
                    color="#0A1B39"
                    mb={1}
                  >
                    Cost{" "}
                    <Text as="span" color="red.500">
                      *
                    </Text>
                  </FormLabel>
                  <Input
                    placeholder="₱0.00"
                    type="number"
                    min={0}
                    step="0.01"
                    bg="white"
                    borderRadius="md"
                    borderColor="gray.200"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    fontSize="xs"
                    h="36px"
                    minH="36px"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                  />
                  <FormErrorMessage fontSize="xs">
                    {errorMessages.price?.message}
                  </FormErrorMessage>
                </FormControl>

                {/* Status Dropdown */}
                <FormControl isRequired isInvalid={!!errorMessages.status}>
                  <FormLabel
                    fontSize="xs"
                    fontWeight="400"
                    color="#0A1B39"
                    mb={1}
                  >
                    Status{" "}
                    <Text as="span" color="red.500">
                      *
                    </Text>
                  </FormLabel>
                  <Select
                    bg="white"
                    borderRadius="md"
                    textColor="gray.900"
                    borderColor="gray.200"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    fontSize="xs"
                    h="36px"
                    minH="36px"
                    value={status}
                    onChange={(e) =>
                      setStatus(e.target.value as "active" | "inactive")
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </Select>
                  <FormErrorMessage fontSize="xs">
                    {errorMessages.status?.message}
                  </FormErrorMessage>
                </FormControl>

                {/* Description */}
                <FormControl isRequired isInvalid={!!errorMessages.description}>
                  <FormLabel
                    fontSize="xs"
                    fontWeight="400"
                    color="#0A1B39"
                    mb={1}
                  >
                    Description
                  </FormLabel>
                  <Textarea
                    placeholder="Type Here"
                    bg="white"
                    borderRadius="md"
                    borderColor="gray.200"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    fontSize="xs"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                    minH="80px"
                    resize="vertical"
                  />
                </FormControl>
              </VStack>
            </Box>

            {/* Desktop/Tablet: Grid layout */}
            <Box display={{ base: "none", sm: "block" }}>
              <VStack spacing={{ base: 3, md: 4 }} align="stretch">
                <Grid
                  templateColumns={{
                    base: "1fr",
                    sm: "repeat(2, 1fr)",
                    md: "repeat(3, 1fr)",
                  }}
                  gap={{ base: 3, md: 4 }}
                >
                  {/* Procedure Name */}
                  <FormControl
                    isRequired
                    isInvalid={!!errorMessages.procedureName}
                  >
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Procedure Name{" "}
                    </FormLabel>
                    <Input
                      placeholder="Enter procedure name"
                      size={{ base: "sm", md: "md" }}
                      fontSize={{ base: "xs", md: "sm" }}
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      h={{ base: "36px", md: "40px" }}
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      value={procedureName}
                      onChange={(e) => setProcedureName(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.procedureName?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Price */}
                  <FormControl isRequired isInvalid={!!errorMessages.price}>
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Cost{" "}
                    </FormLabel>
                    <Input
                      placeholder="₱0.00"
                      type="number"
                      min={0}
                      step="0.01"
                      bg="white"
                      borderRadius="md"
                      borderColor="gray.200"
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      fontSize={{ base: "xs", md: "sm" }}
                      h={{ base: "36px", md: "40px" }}
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                    />
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.price?.message}
                    </FormErrorMessage>
                  </FormControl>

                  {/* Status Dropdown */}
                  <FormControl isRequired isInvalid={!!errorMessages.status}>
                    <FormLabel
                      fontSize={{ base: "xs", md: "sm" }}
                      fontWeight="400"
                      color="#0A1B39"
                      mb={1}
                    >
                      Status{" "}
                    </FormLabel>
                    <Select
                      bg="white"
                      borderRadius="md"
                      textColor="gray.900"
                      borderColor="gray.200"
                      _hover={{ borderColor: "#DC3611" }}
                      _focus={{
                        borderColor: "#DC3611",
                        boxShadow: "0 0 0 1px #DC3611",
                      }}
                      fontSize={{ base: "xs", md: "sm" }}
                      h={{ base: "36px", md: "40px" }}
                      value={status}
                      onChange={(e) =>
                        setStatus(e.target.value as "active" | "inactive")
                      }
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Select>
                    <FormErrorMessage fontSize="xs">
                      {errorMessages.status?.message}
                    </FormErrorMessage>
                  </FormControl>
                </Grid>

                {/* Description */}
                <FormControl isRequired isInvalid={!!errorMessages.description}>
                  <FormLabel
                    fontSize={{ base: "xs", md: "sm" }}
                    fontWeight="400"
                    color="#0A1B39"
                    mb={1}
                  >
                    Description
                  </FormLabel>
                  <Textarea
                    placeholder="Type Here"
                    bg="white"
                    borderRadius="md"
                    borderColor="gray.200"
                    _hover={{ borderColor: "#DC3611" }}
                    _focus={{
                      borderColor: "#DC3611",
                      boxShadow: "0 0 0 1px #DC3611",
                    }}
                    fontSize={{ base: "xs", md: "sm" }}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={4}
                    minH="100px"
                    resize="vertical"
                  />
                </FormControl>
              </VStack>
            </Box>
          </VStack>
        </ModalBody>

        <Box borderBottom="1px solid #E7E8EB" mx={{ base: 4, md: 6 }} />

        <ModalFooter
          py={{ base: 3, md: 4 }}
          px={{ base: 4, md: 6 }}
          gap={{ base: 2, md: 3 }}
          flexWrap="wrap"
        >
          <Stack
            direction={{ base: "column", sm: "row" }}
            spacing={{ base: 2, md: 3 }}
            w="full"
            justify="flex-end"
          >
            <Button
              variant="outline"
              onClick={handleClose}
              w={{ base: "full", sm: "auto" }}
              size={{ base: "sm", md: "md" }}
            >
              Cancel
            </Button>
            <Button
              bg="#DC3611"
              color="white"
              _hover={{ bg: "#bf3c1fff" }}
              onClick={handleSave}
              w={{ base: "full", sm: "auto" }}
              size={{ base: "sm", md: "md" }}
            >
              {editingProcedure ? "Save Changes" : "Create Procedure"}
            </Button>
          </Stack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NewProcedureModal;
